import {
  PlayIcon,
  PlusIcon,
  StopIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import ConfirmationModal from '../components/ConfirmationModal';
import CreateCampaignModal from '../components/CreateCampaignModal';
import LoadingSpinner from '../components/LoadingSpinner';
import campaignsAPI from '../services/campaigns';

const Campaigns = () => {
    const queryClient = useQueryClient();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [campaignToDelete, setCampaignToDelete] = useState(null);

    // Fetch campaigns
    const { data: campaignsData, isLoading: campaignsLoading } = useQuery({
        queryKey: ['campaigns'],
        queryFn: () => campaignsAPI.getCampaigns(),
        refetchInterval: 30000, // Refresh every 30 seconds
    });

    const campaigns = campaignsData?.campaigns || [];

    // Launch campaign mutation
    const launchCampaignMutation = useMutation({
        mutationFn: (campaignId) => campaignsAPI.launchCampaign(campaignId),
        onSuccess: (data) => {
            toast.success(`Campaign launched! Processing ${data.totalContacts} contacts.`);
            queryClient.invalidateQueries({ queryKey: ['campaigns'] });
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to launch campaign');
        },
    });

    // Stop campaign mutation
    const stopCampaignMutation = useMutation({
        mutationFn: (campaignId) => campaignsAPI.stopCampaign(campaignId),
        onSuccess: () => {
            toast.success('Campaign stopped successfully');
            queryClient.invalidateQueries({ queryKey: ['campaigns'] });
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to stop campaign');
        },
    });

    // Delete campaign mutation
    const deleteCampaignMutation = useMutation({
        mutationFn: (campaignId) => campaignsAPI.deleteCampaign(campaignId),
        onSuccess: () => {
            toast.success('Campaign deleted successfully');
            queryClient.invalidateQueries({ queryKey: ['campaigns'] });
            setShowDeleteConfirm(false);
            setCampaignToDelete(null);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to delete campaign');
        },
    });

    const handleLaunch = (campaignId) => {
        launchCampaignMutation.mutate(campaignId);
    };

    const handleStop = (campaignId) => {
        stopCampaignMutation.mutate(campaignId);
    };

    const handleDeleteClick = (campaignId) => {
        setCampaignToDelete(campaignId);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = () => {
        if (campaignToDelete) {
            deleteCampaignMutation.mutate(campaignToDelete);
        }
    };

    if (campaignsLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <LoadingSpinner size="lg" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Create and manage your calling campaigns
                    </p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Create Campaign
                </button>
            </div>

            {/* Campaigns List */}
            {campaigns.length === 0 ? (
                <div className="text-center py-12">
                    <p className="text-gray-500">No campaigns yet. Create your first campaign to get started.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {campaigns.map((campaign) => (
                        <div
                            key={campaign.id}
                            className="bg-white overflow-hidden shadow rounded-lg border border-gray-200"
                        >
                            <div className="p-5">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-lg font-medium text-gray-900 truncate">
                                        {campaign.name}
                                    </h3>
                                    <button
                                        onClick={() => handleDeleteClick(campaign.id)}
                                        className="text-gray-400 hover:text-red-600"
                                        title="Delete campaign"
                                    >
                                        <TrashIcon className="h-5 w-5" />
                                    </button>
                                </div>

                                <div className="space-y-2 text-sm text-gray-600 mb-4">
                                    <div>
                                        <span className="font-medium">From:</span> {campaign.fromNumber || 'N/A'}
                                    </div>
                                    <div>
                                        <span className="font-medium">Contacts:</span> {campaign.contactCount || 0}
                                    </div>
                                    <div>
                                        <span className="font-medium">Calls Made:</span> {campaign.callsMade || 0}
                                    </div>
                                    {campaign.isRunning && (
                                        <div className="text-blue-600">
                                            <span className="font-medium">Progress:</span> {campaign.progress || 0}%
                                        </div>
                                    )}
                                </div>

                                <div className="mt-4">
                                    {campaign.isRunning ? (
                                        <button
                                            onClick={() => handleStop(campaign.id)}
                                            disabled={stopCampaignMutation.isPending}
                                            className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                                        >
                                            <StopIcon className="h-4 w-4 mr-2" />
                                            Stop Campaign
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleLaunch(campaign.id)}
                                            disabled={launchCampaignMutation.isPending || campaign.contactCount === 0}
                                            className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                                        >
                                            <PlayIcon className="h-4 w-4 mr-2" />
                                            Launch Campaign
                                        </button>
                                    )}
                                </div>

                                {campaign.script && (
                                    <div className="mt-3 text-xs text-gray-500">
                                        <p className="truncate" title={campaign.script}>
                                            Script: {campaign.script.substring(0, 50)}...
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Campaign Modal */}
            <CreateCampaignModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteConfirm}
                onClose={() => {
                    setShowDeleteConfirm(false);
                    setCampaignToDelete(null);
                }}
                onConfirm={confirmDelete}
                title="Delete Campaign"
                message="Are you sure you want to delete this campaign? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                confirmButtonClass="bg-red-600 hover:bg-red-700"
            />
        </div>
    );
};

export default Campaigns;
